import { useState } from "react";
import { Pencil, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MAX = 200;
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

export default function AnnotationCell({ cand, onSaved, wide = false }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(cand.annotation || "");
  const [busy, setBusy] = useState(false);
  const id = cand.id;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/candidates/${id}/annotation`, { text });
      onSaved?.(data);
      setEditing(false);
      toast.success(text.trim() ? "Note saved" : "Note cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save note"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className={wide ? "w-full" : "w-56"} data-testid={`annotation-editor-${id}`}>
        <textarea autoFocus value={text} maxLength={MAX} rows={3} data-testid={`annotation-input-${id}`}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } }}
          placeholder="Next steps, e.g. 'Call Monday re: notice period'"
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none" />
        <div className="flex items-center justify-between mt-1">
          <span className={`text-[11px] ${text.length >= MAX ? "text-rose-600" : "text-slate-400"}`} data-testid={`annotation-count-${id}`}>{text.length}/{MAX}</span>
          <div className="flex gap-1.5">
            <button onClick={() => { setText(cand.annotation || ""); setEditing(false); }} className="text-[11px] px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50" data-testid={`annotation-cancel-${id}`}>Cancel</button>
            <button onClick={save} disabled={busy} className="text-[11px] px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60" data-testid={`annotation-save-${id}`}>{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    );
  }

  if (!cand.annotation) {
    return (
      <button onClick={() => { setText(""); setEditing(true); }} data-testid={`annotation-add-${id}`}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-indigo-600">
        <StickyNote className="w-3.5 h-3.5" /> Add note
      </button>
    );
  }
  return (
    <button onClick={() => { setText(cand.annotation); setEditing(true); }} data-testid={`annotation-view-${id}`}
      title={`${cand.annotation}\n— ${cand.annotation_by || ""}, ${when(cand.annotation_at)}`}
      className={`group text-left ${wide ? "w-full" : "w-56"}`}>
      <span className="block text-xs text-slate-700 line-clamp-2 break-words" data-testid={`annotation-text-${id}`}>{cand.annotation}</span>
      <span className="flex items-center gap-1 text-[11px] text-slate-400 mt-0.5">
        {cand.annotation_by} · {when(cand.annotation_at)} <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100" />
      </span>
    </button>
  );
}
